import { RequestHeaders, RequestParams } from '@bv/api';
import { WithPagination } from '@bv/api/crud';
import { getCookie } from '@bv/helpers/api';

import type { StepConfig, StepFormField, FieldValue, FieldValues } from '../../types/workflow';

class UsersApi extends WithPagination {
  constructor() {
    super('conversations/users');
  }
}

class TeamsApi extends WithPagination {
  constructor() {
    super('iam/teams');
  }
}

const usersApi = new UsersApi();
const teamsApi = new TeamsApi();
const workspaceId = getCookie('x-workspace-id');

const maxValues = {
  seconds: 86400,
  minutes: 1440,
  hours: 24,
};

const fetchUsersOptions = async (config?: { params?: RequestParams; headers?: RequestHeaders }) => {
  const { data, meta } = await usersApi.paginated({
    ...config,
    params: {
      ...config?.params,
      userType: 'normal',
    },
  });

  return {
    meta,
    data: (data as Array<{ username: string; email: string; id: string }>).map((user) => ({
      label: user.username || user.email,
      value: user.id,
    })),
  };
};

const fetchAiAgentsOptions = async (config?: { params?: RequestParams; headers?: RequestHeaders }) => {
  const { data, meta } = await usersApi.paginated({
    ...config,
    params: {
      ...config?.params,
      userType: 'ai_agent',
    },
  });

  return {
    meta,
    data: (data as Array<{ username: string; email: string; id: string }>).map((agent) => ({
      label: agent.username || agent.email,
      value: agent.id,
    })),
  };
};

const fetchTeamsOptions = async (config?: { params?: RequestParams; headers?: RequestHeaders }) => {
  const { data, meta } = await teamsApi.paginated(config);

  return {
    meta,
    data: (data as Array<{ name: string; id: string }>).map((team) => ({
      label: team.name,
      value: team.id,
    })),
  };
};

const assign: StepConfig = {
  // Design
  icon: 'assign',
  headerTextColor: '#299E27',
  headerColor: '#E2FAD4',
  title: 'Assign To',
  text: 'Assign to specific user',
  service: 'bbc',
  category: 'Router',
  desc: 'Allows a contact to be assigned to a specific user, user in a team, user in a workspace, or otherwise unassign it.',
  canAdd: (nodeData: Record<string, any>) => {
    return nodeData.assignmentType !== 'aiAgent';
  },
  primaryKey: (parentValues: Record<string, any>) => {
    const { assignmentType, user, team } = parentValues;
    if (assignmentType === 'agent') {
      return user ? `User: ${user.name}` : 'User';
    }
    if (assignmentType === 'team') {
      return team ? `Team: ${team.name}` : 'Team';
    }
    if (assignmentType === 'aiAgent') {
      return user ? `AI Agent: ${user.name}` : 'AI Agent';
    }
    if (assignmentType === 'none') {
      return 'unassigned';
    }
    return assignmentType;
  },
  connectors: {
    SuccessAssignment: {
      headerTextColor: '#299E27',
      headerColor: '#E2FAD4',
      title: 'Success',
      category: 'Connector',
      show: (nodeData: Record<string, any>) => {
        return nodeData.assignmentType !== 'aiAgent';
      },
    },
    FailedAssignment: {
      headerTextColor: '#299E27',
      headerColor: '#E2FAD4',
      title: 'Failure : Invalid',
      category: 'Connector',
      show: (nodeData: Record<string, any>) => {
        return nodeData.assignmentType !== 'aiAgent';
      },
    },
    Timeout: {
      headerTextColor: '#299E27',
      headerColor: '#E2FAD4',
      title: 'Failure:Timeout',
      category: 'Connector',
      show: (nodeData: Record<string, any>) => {
        return nodeData.assignmentType !== 'aiAgent' && nodeData.timeout?.hasTimeout === true;
      },
    },
  },

  // Form
  formFields: [
    {
      key: 'assignmentType',
      label: 'Assignment Type',
      type: 'select',
      returnObject: false,
      defaultValue: 'agent',
      options: [
        { label: 'Assign to a specific user', value: 'agent' },
        { label: 'Assign to a user in specific team', value: 'team' },
        { label: 'Assign To a User In The Workspace', value: 'workspace' },
        { label: 'AI Agents', value: 'aiAgent' },
        { label: 'Unassign', value: 'none' },
      ],
      onChange: (value: FieldValue, allValues: FieldValues, allFields: Record<string, StepFormField>) => {
        if (value === 'none') {
          allFields.user.show = false;
          allFields.team.show = false;
          allFields.strategy.show = false;
          allFields['advancedStrategy.enabled'].show = false;
          allValues.user = null;
          allValues.team = null;
        } else if (value === 'agent') {
          allFields.user.show = true;
          allFields.user.label = 'Select User';
          allFields.team.show = false;
          allFields.strategy.show = false;
          allFields['advancedStrategy.enabled'].show = false;
          allValues.user = null;
          allValues.team = null;
        } else if (value === 'team') {
          allFields.user.show = false;
          allFields.team.show = true;
          allFields.strategy.show = true;
          allValues.user = null;
        } else if (value === 'workspace') {
          allFields.user.show = false;
          allFields.team.show = false;
          allFields.strategy.show = true;
          allValues.user = null;
          allValues.team = null;
        } else if (value === 'aiAgent') {
          allFields.user.show = true;
          allFields.user.label = 'Select AI Agent';
          allFields.team.show = false;
          allFields.strategy.show = false;
          allFields['advancedStrategy.enabled'].show = false;
          allValues.user = null;
          allValues.team = null;
        }
      },
    },
    {
      key: 'user',
      label: 'Select User',
      type: 'select',
      returnObject: true,
      show: (parentValues: Record<string, any>) => {
        return parentValues.assignmentType === 'agent' || parentValues.assignmentType === 'aiAgent';
      },
      dependsOn: ['assignmentType'],
      validation: {
        required: true,
      },
      options: (parentValues: Record<string, any>) => {
        if (parentValues.assignmentType === 'aiAgent') {
          return fetchAiAgentsOptions;
        }
        return fetchUsersOptions;
      },
    },
    {
      key: 'team',
      label: 'Select Team',
      type: 'select',
      returnObject: true,
      show: (parentValues: Record<string, any>) => {
        return parentValues.assignmentType === 'team';
      },
      searchKey: 'q',
      dependsOn: ['assignmentType'],
      validation: {
        required: true,
      },
      options: () => {
        return fetchTeamsOptions;
      },
    },

    {
      key: 'workspaceId',
      type: 'text',
      defaultValue: workspaceId || '',
      show: false,
    },
    {
      key: 'strategy',
      label: 'Assignment Logic',
      type: 'radio',
      defaultValue: 'roundRobin',
      dependsOn: ['assignmentType'],
      show: (parentValues: Record<string, any>) => {
        return parentValues.assignmentType === 'team' || parentValues.assignmentType === 'workspace';
      },
      options: [
        { label: 'Assign to users round robin', value: 'roundRobin' },
        { label: 'Assign to the user with least open conversations', value: 'leastOpenConversation' },
      ],
    },
    {
      key: 'advancedStrategy.enabled',
      label: 'Advanced Settings',
      type: 'accordion',
      defaultValue: false,
      show: (parentValues: Record<string, any>) => {
        return parentValues.assignmentType === 'team' || parentValues.assignmentType === 'workspace';
      },
      fields: [
        {
          key: 'advancedStrategy.user_online',
          label: 'Only assign to online users',
          type: 'toggle',
          defaultValue: false,
        },
        {
          key: 'advancedStrategy.fewer_conversations',
          label: 'Only assign to users fewer than:',
          type: 'toggle',
          defaultValue: false,
          onChange: (value: boolean, allValues: FieldValues, allFields: Record<string, StepFormField>) => {
            if (value) {
              allFields['advancedStrategy.fewerThanConversationsCount'].show = true;
            } else {
              allFields['advancedStrategy.fewerThanConversationsCount'].show = false;
            }
          },
        },
        {
          key: 'advancedStrategy.fewerThanConversationsCount',
          label: 'Number of conversations',
          type: 'number',
          defaultValue: 15,
          show: false,
          dependsOn: ['advancedStrategy.fewer_conversations'],
          validation: {
            required: true,
            numeric: true,
            integer: true,
            minValue: 1,
          },
        },
        {
          key: 'timeout.hasTimeout',
          label: 'Add Timeout Branch',
          type: 'toggle',
          defaultValue: false,
          onChange: (value: boolean, allValues: FieldValues, allFields: Record<string, StepFormField>) => {
            if (value) {
              allFields['timeout.unit'].show = true;
              allFields['timeout.value'].show = true;
            } else {
              allFields['timeout.unit'].show = false;
              allFields['timeout.value'].show = false;
            }
          },
        },
        {
          key: 'timeout.unit',
          label: 'Unit',
          type: 'select',
          defaultValue: 'minutes',
          show: false,
          dependsOn: ['timeout.hasTimeout'],
          options: [
            { label: 'Seconds', value: 'seconds' },
            { label: 'Minutes', value: 'minutes' },
            { label: 'Hours', value: 'hours' },
          ],
          onChange: (value: FieldValue, allValues: FieldValues, allFields: Record<string, StepFormField>) => {
            if (value === 'seconds') {
              if (allFields['timeout.value'] && allFields['timeout.value'].validation) {
                allFields['timeout.value'].validation.maxValue = maxValues.seconds;
              }
            } else if (value === 'minutes') {
              if (allFields['timeout.value'] && allFields['timeout.value'].validation) {
                allFields['timeout.value'].validation.maxValue = maxValues.minutes;
              }
            } else {
              if (allFields['timeout.value'] && allFields['timeout.value'].validation) {
                allFields['timeout.value'].validation.maxValue = maxValues.hours;
              }
            }
          },
        },
        {
          key: 'timeout.value',
          label: 'Timeout',
          type: 'number',
          defaultValue: 20,
          show: false,
          dependsOn: ['timeout.hasTimeout', 'timeout.unit'],
          validation: {
            required: true,
            numeric: true,
            integer: true,
            minValue: 1,
            maxValue: maxValues.minutes,
          },
        },
      ],
    },
  ],
};

export default assign;
